import React, { useState, useMemo } from "react";
import { Search } from "lucide-react";
import { MetricCard } from "../ui/MetricCard";
import { FaAngleRight, FaArrowLeft } from "react-icons/fa";
import Tooltip from "@mui/material/Tooltip";
import Select from "react-select";
import "./SubdomainDetail.scss";
import Layer1 from "../../../assests/Layer_1.png";
import group1 from "../../../assests/Vector.png";
import circle from "../../../assests/Group3702.png";
import ProductLogo from "../../ProductLogo/ProductLogo";
import LogoImage from "../../ProductLogo/LogoImage";
import ERLandingPage from "../ERDiagram/pages/ERLandingPage";

// Data Layer options
const DATA_LAYER_OPTIONS = [
  { value: "ingestion", label: "Ingestion" },
  { value: "bronze", label: "Bronze" },
  { value: "silver", label: "Silver" },
  { value: "gold", label: "Gold" },
  { value: "analyticalProduct", label: "Analytical Product" },
];

export const ProductPage = ({
  subdomain,
  onBack,
  onL4Click,
  regions = [],
  appliedFilters,
  onFilterChange,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  // Initialize filter states from appliedFilters prop (like DomainOverview)
  const initialSelectedRegions = useMemo(() => {
    if (appliedFilters?.regions && appliedFilters.regions.length > 0) {
      return appliedFilters.regions.map((region) => ({
        value: region,
        label: region,
      }));
    }
    return [];
  }, []); // Empty deps - only compute on mount

  const initialDataLayer = useMemo(() => {
    if (appliedFilters?.dataLayer && appliedFilters.dataLayer.length > 0) {
      // Handle dataLayer as array
      const DATA_LAYER_OPTIONS_CONST = [
        { value: "ingestion", label: "Ingestion" },
        { value: "bronze", label: "Bronze" },
        { value: "silver", label: "Silver" },
        { value: "gold", label: "Gold" },
        { value: "analyticalProduct", label: "Analytical Product" },
      ];
      return appliedFilters.dataLayer
        .map((layerValue) =>
          DATA_LAYER_OPTIONS_CONST.find(
            (option) => option.value === layerValue,
          ),
        )
        .filter(Boolean);
    }
    return [];
  }, []); // Empty deps - only compute on mount

  const initialFiltersApplied = useMemo(() => {
    return appliedFilters?.regions?.length > 0 ||
      appliedFilters?.dataLayer?.length > 0
      ? true
      : false;
  }, []); // Empty deps - only compute on mount

  const initialShowFilters = useMemo(() => {
    return initialFiltersApplied;
  }, []); // Empty deps - only compute on mount

  // Filter states - initialized from appliedFilters
  const [showFilters, setShowFilters] = useState(initialShowFilters);
  const [selectedRegions, setSelectedRegions] = useState(
    initialSelectedRegions,
  );
  const [selectedDataLayer, setSelectedDataLayer] = useState(initialDataLayer);
  const [filtersApplied, setFiltersApplied] = useState(initialFiltersApplied);

  const rowsPerPage = 30;

  // Convert regions to react-select options
  const regionOptions = useMemo(() => {
    return regions.map((region) => ({ value: region, label: region }));
  }, [regions]);

  // Handle region selection (multiselect)
  const handleRegionChange = (selectedOptions) => {
    setSelectedRegions(selectedOptions || []);
  };

  // Handle data layer selection (multiselect)
  const handleDataLayerChange = (selectedOptions) => {
    setSelectedDataLayer(selectedOptions || []);
  };

  // Apply filters
  const handleApplyFilters = () => {
    if (onFilterChange) {
      const regionValues = selectedRegions.map((r) => r.value);
      const dataLayerValues = Array.isArray(selectedDataLayer)
        ? selectedDataLayer.map((d) => d.value)
        : [];
      onFilterChange(regionValues, dataLayerValues);
      setFiltersApplied(true);
    }
  };

  const filteredRows =
    subdomain?.Products?.filter((sub) =>
      sub.product_name.toLowerCase().includes(searchQuery.toLowerCase()),
    ) || [];
  console.log("filteredRows", filteredRows);

  const totalRows = filteredRows.length;
  const totalPages = Math.ceil(totalRows / rowsPerPage);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredRows.slice(indexOfFirstRow, indexOfLastRow);

  return (
    <div className="space-y-8">
      {/* Subdomain Summary */}
      <div className="rounded-lg p-4 text-white">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-black mb-4">{subdomain.name}</h1>
           
          </div>
          <div className="flex flex-col md:flex-row items-center space-x-3 gap-3 mt-2 md:mt-0 flex-shrink-0">
            <ERLandingPage />
            <button
              onClick={onBack}
              className="h-[40px] text-[#bb1ccc] hover:text-white transition-colors duration-200 border border-[#bb1ccc] p-2 rounded-lg flex items-center gap-2 hover:bg-[#bb1ccc] hover:shadow-lg px-4 whitespace-nowrap"
            >
              <FaArrowLeft /> Go Back
            </button>
          </div>
        </div>
        <p className="text-lg mb-3 text-black leading-relaxed">
              {subdomain.description}
            </p>

        {/* Summary Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <MetricCard
            title="Total Product"
            value={subdomain?.product_count ? subdomain?.product_count : "0"}
            variant="success"
          />

          <MetricCard
            title="Quality Score"
            value={subdomain?.score ? subdomain?.score : "0%"}
            variant="warning"
          />
          <MetricCard
            title="Total Assets"
            value={subdomain?.assets ? subdomain?.assets : "0"}
            variant="primary"
          />
        </div>
      </div>

      {/* Products Section - Always show to display empty state */}
      {subdomain && (
        <div className="space-y-6">
          {/* Search and Filter Controls */}
          <div className="space-y-4">
            {/* First Row: Title, Search, Show Filters button */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
              <h2 className="text-2xl font-bold text-gray-900">Products</h2>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full md:w-auto">
                <div className="relative w-full sm:w-auto">
                  <Search
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    size={20}
                  />
                  <input
                    type="text"
                    placeholder="Search Products..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1); // Reset page when search updates
                    }}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-[45px]"
                  />
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors h-[45px] whitespace-nowrap ${
                    showFilters || filtersApplied
                      ? "bg-[#bb1ccc] text-white border-[#bb1ccc]"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <span>{showFilters ? "Hide Filters" : "Show Filters"}</span>
                </button>
              </div>
            </div>

            {/* Second Row: Filters (Collapsible) */}
            {showFilters && (
              <div className="flex flex-col md:flex-row items-start md:items-center gap-3 bg-gray-50 p-4 rounded-lg border border-gray-200">
                {/* Region Multiselect */}
                <div className="w-full md:flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Region
                  </label>
                  <Select
                    isMulti
                    value={selectedRegions}
                    onChange={handleRegionChange}
                    options={regionOptions}
                    placeholder="Select regions..."
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "45px",
                        borderColor: "#d1d5db",
                        "&:hover": {
                          borderColor: "#9ca3af",
                        },
                      }),
                      multiValue: (base) => ({
                        ...base,
                        backgroundColor: "#e0e7ff",
                        borderRadius: "4px",
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: "#3730a3",
                        fontWeight: "500",
                      }),
                      multiValueRemove: (base) => ({
                        ...base,
                        color: "#3730a3",
                        "&:hover": {
                          backgroundColor: "#c7d2fe",
                          color: "#312e81",
                        },
                      }),
                    }}
                  />
                </div>

                {/* Data Layer Multiselect */}
                <div className="w-full md:flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data Layer
                  </label>
                  <Select
                    isMulti
                    value={selectedDataLayer}
                    onChange={handleDataLayerChange}
                    options={DATA_LAYER_OPTIONS}
                    placeholder="Select data layers..."
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "45px",
                        borderColor: "#d1d5db",
                        "&:hover": {
                          borderColor: "#9ca3af",
                        },
                      }),
                      multiValue: (base) => ({
                        ...base,
                        backgroundColor: "#e0e7ff",
                        borderRadius: "4px",
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: "#3730a3",
                        fontWeight: "500",
                      }),
                      multiValueRemove: (base) => ({
                        ...base,
                        color: "#3730a3",
                        "&:hover": {
                          backgroundColor: "#c7d2fe",
                          color: "#312e81",
                        },
                      }),
                    }}
                  />
                </div>

                {/* Apply Filters Button */}
                <div className="flex items-end w-full md:w-auto" style={{ paddingTop: "24px" }}>
                  <button
                    onClick={handleApplyFilters}
                    disabled={
                      selectedRegions.length === 0 &&
                      selectedDataLayer.length === 0
                    }
                    className="px-6 py-2.5 text-sm font-medium text-white bg-[#bb1ccc] rounded-lg hover:bg-[#000080] transition-colors disabled:opacity-50 disabled:cursor-not-allowed h-[45px] whitespace-nowrap"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Empty State Message */}
          {currentRows.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="text-center">
                <div className="mb-4">
                  <svg
                    className="mx-auto h-16 w-16 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Products Found
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  {filtersApplied
                    ? "No products match your current filter criteria. Try adjusting your filters."
                    : "There are no products available at this time."}
                </p>
                {filtersApplied && (
                  <button
                    onClick={() => {
                      setSelectedRegions([]);
                      setSelectedDataLayer([]);
                      setFiltersApplied(false);
                      if (onFilterChange) {
                        onFilterChange([], []);
                      }
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#bb1ccc] hover:bg-[#000080] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#bb1ccc]"
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Results Grid */}
          {currentRows.length > 0 && (
            <div className="subdomain-gridbox">
              {currentRows.map((l3Subdomain) => {
                return (
                  <div
                    key={l3Subdomain.name}
                    onClick={
                      l3Subdomain?.product_assets_detail?.length > 0
                        ? () => onL4Click(l3Subdomain.product_name)
                        : undefined
                    }
                    className={`bg-white rounded-lg border border-gray-200  transition-all duration-200 ${
                      l3Subdomain?.product_assets_detail?.length > 0
                        ? "cursor-pointer hover:shadow-lg"
                        : ""
                    }`}
                  >
                    <div
                      style={{ width: "100%" }}
                      className="flex justify-between bg-gray-100 border-b border-gray-200 p-4"
                    >
                      <div className="flex-1">
                        <div className="flex justify-between items-center w-full">
                          <div
                            style={{ display: "grid" }}
                            className="items-center gap-1"
                          >
                            <div className="text-xl font-semibold text-gray-900 case capitalize flex items-center">
                              <Tooltip
                                title="Data Steward Certified"
                                placement="top"
                              >
                                <img src={Layer1} className="mr-[5px]" />
                              </Tooltip>
                              <span>{l3Subdomain.product_name}</span>
                            </div>

                            <p
                              className="text-black mb-1 text-sm  pl-2 pr-4 line-clamp-1  overflow"
                              title={l3Subdomain.product_description}
                            >
                              {l3Subdomain.product_description ||
                                "Not Available"}
                            </p>

                            <div className="flex items-center gap-2 justify-start mt-1">
                              <div className="text-gray-500 h-[10px]"></div>
                              {(l3Subdomain.product_layer?.toLowerCase() === "bronze" 
                                || l3Subdomain.product_layer?.toLowerCase() === "ingestion") 
                              && // <ProductLogo productId={l3Subdomain.product_id} />
                                <div className="ml-1 inline-flex items-center gap-2">
                                  {l3Subdomain.logoIds?.map((l) => (
                                    <LogoImage key={l} logoId={l} />
                                  ))}
                                </div>
                              }
                              <div
                                style={{
                                  fontSize: "10px",
                                  background:
                                    l3Subdomain.product_layer?.toLowerCase() ===
                                    "gold"
                                      ? "#FFD700"
                                      : l3Subdomain.product_layer?.toLowerCase() ===
                                          "silver"
                                        ? "#C0C0C0"
                                        : l3Subdomain.product_layer?.toLowerCase() ===
                                            "bronze"
                                          ? "#CD7F32"
                                          : "#e0e0e0",
                                  color:
                                    l3Subdomain.product_layer?.toLowerCase() ===
                                    "gold"
                                      ? "#8B8000"
                                      : l3Subdomain.product_layer?.toLowerCase() ===
                                          "silver"
                                        ? "#4F4F4F"
                                        : l3Subdomain.product_layer?.toLowerCase() ===
                                            "bronze"
                                          ? "#ffffff"
                                          : "#000000",
                                }}
                                className="py-1 px-3 rounded-xl capitalize"
                              >
                                {l3Subdomain.product_layer || "N/A"}
                              </div>
                            </div>
                          </div>

                          {l3Subdomain &&
                            l3Subdomain.product_assets_detail &&
                            l3Subdomain.product_assets_detail.length > 0 && (
                              <div
                                onClick={() =>
                                  onL4Click(l3Subdomain?.product_name)
                                }
                              >
                                <FaAngleRight
                                  style={{
                                    fontSize: "20px",
                                    cursor: "pointer",
                                  }}
                                  className="text-gray-400"
                                />
                              </div>
                            )}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-2  pl-4 pr-4 pt-[20px] pb-1 ">
                      <div className="flex items-center space-x-4">
                        <div className="p-2 rounded-lg">
                          <img
                            src={circle}
                            className="bg-[#FF2897] p-3 h-[50px]"
                            alt="icon"
                          />
                        </div>
                        <div>
                          <div
                            style={{ fontSize: "13px" }}
                            className="text-black"
                          >
                            Assets
                          </div>
                          <div className="font-semibold text-sm text-gray-900">
                            {l3Subdomain.product_assets}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-4">
                        <div className="p-3 rounded-lg">
                          <img
                            src={group1}
                            className="bg-[#63B945] p-3 h-[50px] "
                          />
                        </div>
                        <div>
                          <div
                            style={{ fontSize: "13px" }}
                            className="text-black"
                          >
                            CDE
                          </div>
                          <div className="font-semibold text-sm text-gray-900">
                            {l3Subdomain.product_cde_count}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Quality Score Bar */}
                    <div className="mt-2 p-4 ">
                      <div className="flex items-center justify-between  mb-1">
                        <span className="text-black">Quality Score</span>
                        <span className="text-black">
                          {l3Subdomain.product_score}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${
                            parseFloat(l3Subdomain.product_score) > 95
                              ? "bg-green-700"
                              : parseFloat(l3Subdomain.product_score) < 90
                                ? "bg-red-500"
                                : parseFloat(l3Subdomain.product_score) > 90
                                  ? "bg-yellow-700"
                                  : "bg-gray-700"
                          }`}
                          style={{
                            width: `${parseFloat(l3Subdomain.product_score)}%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Pagination - only show when there's data */}
      {currentRows.length > 0 && (
        <div className="pagination flex flex-wrap md:flex-nowrap justify-between items-center gap-3 mt-4 px-4">
          <span className="text-sm text-gray-600">
            Showing {indexOfFirstRow + 1} to{" "}
            {Math.min(indexOfLastRow, totalRows)} of {totalRows} entries
          </span>
          <div className="pagination-controls flex flex-wrap gap-1">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
              className="px-2 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 transition-colors duration-200"
            >
              Previous
            </button>
            {[...Array(totalPages)].map((_, idx) => (
              <button
                key={idx}
                className={`px-3 py-1 border rounded transition-colors duration-200 ${
                  currentPage === idx + 1
                    ? "bg-blue-500 text-white"
                    : "hover:bg-gray-50"
                }`}
                onClick={() => setCurrentPage(idx + 1)}
              >
                {idx + 1}
              </button>
            ))}
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
              className="px-2 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 transition-colors duration-200"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
